import './ui/styles.css';
import { buildReference, wireReferenceTabs, selectRefTab } from './screens/reference.js';
import { showTutorial } from './screens/tutorial.js';
import * as localGame from './screens/game.js';
import * as onlineGame from './screens/online-game.js';
import { showLobbyPlaceholder } from './screens/lobby.js';
import { toggleKenBot, hideKenBot } from './screens/kenbot.js';
import { showHands, wireHands } from './screens/hands.js';
import { showChat, wireChat } from './screens/chat.js';
import { showAdvisor } from './screens/advisor.js';
import { toggleEnabled as toggleSound, isEnabled as isSoundEnabled } from './ui/sound.js';
import { initTheme, mountThemePicker, closeThemePicker } from './ui/theme.js';

// Game mode selector. All tile-tap / button-press handlers route through the
// currently active driver.
let mode = 'local';
let driver = localGame;
let refFrom = 'menu';
let difficulty = 'beginner';

function showScreen(id) {
  closeThemePicker();
  document.querySelectorAll('.screen').forEach((s) => s.classList.remove('active'));
  document.getElementById(id).classList.add('active');
}

const showMenu = () => showScreen('menu');
const showSetup = () => showScreen('setup');
function showReference(from = 'menu') {
  refFrom = from;
  // From in-game, default to the Hands tab — patterns are the most useful
  // reference mid-play. From the menu, keep the Tiles tab as the landing.
  selectRefTab(from === 'game' ? 'ref-patterns' : 'ref-tiles');
  showScreen('reference');
}
function showLobby() {
  showScreen('lobby');
  showLobbyPlaceholder(onOnlineStart);
}

function onOnlineStart(gameId, seat) {
  mode = 'online';
  driver = onlineGame;
  showScreen('game');
  refFrom = 'game';
  onlineGame.startOnlineGame({ gameId, seat, onExit: showMenu });
}

function startLocal() {
  mode = 'local';
  driver = localGame;
  localGame.hideWin();
  showScreen('game');
  refFrom = 'game';
  localGame.startLocalGame({ difficulty, onExit: showMenu });
}

function wireMenu() {
  document.getElementById('btnLearn').addEventListener('click', () => {
    showScreen('tutorial');
    showTutorial(showMenu);
  });
  document.getElementById('btnPractice').addEventListener('click', showSetup);
  document.getElementById('btnOnline').addEventListener('click', showLobby);
  document.getElementById('btnQuickRef').addEventListener('click', () => showReference('menu'));
  document.getElementById('btnAdvisor').addEventListener('click', () => {
    showScreen('advisor');
    showAdvisor(showMenu);
  });
}

function wireSetup() {
  document.querySelectorAll('.diff-btn').forEach((btn) => {
    btn.addEventListener('click', () => {
      document.querySelectorAll('.diff-btn').forEach((b) => b.classList.remove('sel'));
      btn.classList.add('sel');
      difficulty = btn.dataset.d;
    });
  });
  document.getElementById('btnSetupBack').addEventListener('click', showMenu);
  document.getElementById('btnSetupStart').addEventListener('click', startLocal);
}

function wireReference() {
  wireReferenceTabs();
  document.getElementById('btnRefBack').addEventListener('click', () => {
    showScreen(refFrom === 'game' ? 'game' : 'menu');
  });
}

function wireTutorial() {
  document.getElementById('btnTutBack').addEventListener('click', showMenu);
}

function exitToMenu() {
  if (mode === 'online') onlineGame.exitOnline();
  else localGame.exitGame();
  hideKenBot(); // help is game-screen-only; don't let him float over the menu
  showMenu();
}

function wireGame() {
  document.getElementById('btnGameRef').addEventListener('click', () => showReference('game'));
  document.getElementById('btnGameHome').addEventListener('click', exitToMenu);
  document.getElementById('btnAskHelp').addEventListener('click', toggleKenBot);
  document.getElementById('btnShowHands').addEventListener('click', showHands);
  document.getElementById('btnChatToggle').addEventListener('click', showChat);

  const soundBtn = document.getElementById('btnSoundToggle');
  const soundIcon = document.getElementById('iconSound');
  const syncIcon = () => {
    soundIcon.textContent = isSoundEnabled() ? '🔊' : '🔇';
    soundBtn.title = isSoundEnabled() ? 'Sound on — click to mute' : 'Sound off — click to unmute';
  };
  syncIcon();
  soundBtn.addEventListener('click', () => {
    toggleSound();
    syncIcon();
  });
  document.getElementById('btnDraw').addEventListener('click', () => driver.playerDraw());
  document.getElementById('btnDiscard').addEventListener('click', () => driver.playerDiscard());
  document.getElementById('btnSwap').addEventListener('click', () => driver.showSwapOptions?.());
  document.getElementById('btnHint').addEventListener('click', () => driver.showHint());
  document.getElementById('btnMahjong').addEventListener('click', () => driver.declareMahjong());

  // Clicking the 🎯 badge cycles the hint same as the 💡 button.
  document.getElementById('phaseLabel').addEventListener('click', (e) => {
    if (e.target.closest('.target-badge')) driver.showHint();
  });

  document.getElementById('handTiles').addEventListener('click', (e) => {
    const el = e.target.closest('[data-tile-idx]');
    if (el) driver.selectTileAt(Number(el.dataset.tileIdx));
  });
  document.getElementById('charTiles').addEventListener('click', (e) => {
    const el = e.target.closest('[data-char-idx]');
    if (el) driver.charPickTile(Number(el.dataset.charIdx));
  });
  document.getElementById('charBtn').addEventListener('click', () => driver.charPass());
  document.getElementById('charSkipBtn').addEventListener('click', () => driver.charSkip?.());

  document.getElementById('btnWinPlayAgain').addEventListener('click', () => {
    if (mode === 'online') {
      // No "play again" in online v1 — drop back to the lobby.
      onlineGame.hideOnlineWin();
      onlineGame.exitOnline();
      showMenu();
      return;
    }
    startLocal();
  });
  document.getElementById('btnWinMenu').addEventListener('click', () => {
    if (mode === 'online') onlineGame.hideOnlineWin();
    else localGame.hideWin();
    exitToMenu();
  });
}

function wireLobby() {
  document.getElementById('btnLobbyBack').addEventListener('click', showMenu);
}

function init() {
  initTheme();
  mountThemePicker();
  buildReference();
  wireMenu();
  wireSetup();
  wireReference();
  wireTutorial();
  wireGame();
  wireLobby();
  wireHands();
  wireChat();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}
